/**
 * The Huddles area (logic/huddlesArea): live, upcoming and recent huddles and my links, its three ways in — "Start a
 * huddle", "Get a link to share" and "Schedule" — and "Join with a link or code". It works with Chat off: a huddle
 * with people but no conversation is a link huddle (`meeting:<code>`) they're invited to. Joining goes through the
 * call view model, which joins link huddles inside the app. Online only.
 */
import type { CreateMeetingRequest, Huddle, HuddleRecordResponse, HuddlesOverviewResponse, Meeting, MeetingPolicy } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { isFeatureEnabled } from "../logic/features.ts";
import { defaultMeetingPolicy, huddleLinkSettings, huddlesSections, liveHuddleOf, meetingPolicyChoices, parseMeetingCode, type HuddlesSections } from "../logic/huddlesArea.ts";
import { memberName } from "../logic/workspace.ts";
import type { HuddleApi } from "../ports.ts";
import type { HuddleViewModel } from "./HuddleViewModel.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** "Get a link to share" (now) or "Schedule" (a day and time). */
export type HuddleLinkForm = {
  mode: "link" | "schedule";
  title: string;
  /** "YYYY-MM-DD", local (Schedule). */
  date: string;
  /** "HH:MM", local (Schedule). */
  time: string;
  policy: MeetingPolicy;
  /** Members invited (Schedule): it shows in their Upcoming. */
  invitees: string[];
  busy: boolean;
  error?: MessageDescriptor;
  /** Made: the link to copy and share. */
  created?: Meeting;
};

/** "Start a huddle": with people, a channel, or nobody yet (a link to share). */
export type HuddleStartForm = {
  userIds: string[];
  /** A channel to huddle in (Chat on only). */
  channelId?: string;
  query: string;
  busy: boolean;
  error?: MessageDescriptor;
};

export type HuddlesViewState = {
  status: "loading" | "ready" | "failed";
  sections: HuddlesSections;
  /** "Who can join" choices for links, after Settings → Huddles. */
  policyChoices: MeetingPolicy[];
  /** Links to share are on (Settings → Huddles). */
  linksOn: boolean;
  /** Chat is on: people and channels start a conversation huddle; off, a link huddle with invitees. */
  chatOn: boolean;
  /** "Join with a link or code". */
  joinText: string;
  joinError?: MessageDescriptor;
  link?: HuddleLinkForm;
  start?: HuddleStartForm;
  /** A past huddle's page. */
  record?: { huddleId: string; status: "loading" | "ready" | "failed"; data?: HuddleRecordResponse };
};

/** Where joining leads: the call (its conversation key, for the huddle tab) or a link page to open. */
export type HuddleDestination = { kind: "call"; channelKey: string } | { kind: "page"; url: string };

const EMPTY: HuddlesOverviewResponse = { live: [], links: [], recent: [] };

const HALF_HOUR_MS = 30 * 60_000;
/** A past huddle's page loads again this often while its AI notes are being written, at most NOTES_POLLS times. */
const NOTES_POLL_MS = 10_000;
const NOTES_POLLS = 18;

/** "2026-10-27" + "14:30" in local time → epoch ms (undefined when malformed). */
export function localDateTime(date: string, time: string): number | undefined {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  const t = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (d === null || t === null) return undefined;
  const h = Number(t[1]);
  const m = Number(t[2]);
  if (h > 23 || m > 59) return undefined;
  const at = new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3]), h, m);
  return Number.isNaN(at.getTime()) ? undefined : at.getTime();
}

export class HuddlesViewModel extends ViewModel<HuddlesViewState> {
  private overview: HuddlesOverviewResponse = EMPTY;
  private inFlight: Promise<void> | undefined;
  private knownHuddles: Record<string, Huddle> | undefined;
  private cancelNotesPoll: (() => void) | undefined;

  constructor(
    private readonly ws: WorkspaceViewModel,
    private readonly call: HuddleViewModel,
    private readonly api: HuddleApi,
    /** Calls run in this app; false where they open in the system browser (the Linux desktop app). */
    private readonly joinHere = true,
  ) {
    super({ status: "loading", sections: { live: [], upcoming: [], recent: [], myLinks: [] }, policyChoices: ["ask", "open", "workspace"], linksOn: true, chatOn: true, joinText: "" });
    this.recompute();
    this.onDispose(() => this.cancelNotesPoll?.());
    // Live huddles arrive in bootstrap and huddle.* events: re-section when they change; a huddle that ended shows in
    // Recent after a refetch.
    this.onDispose(
      ws.subscribe(() => {
        const huddles = ws.state.cache?.huddles;
        if (huddles === this.knownHuddles) return;
        const ids = new Set(Object.values(huddles ?? {}).map((h) => h.id));
        const ended = Object.values(this.knownHuddles ?? {}).some((h) => !ids.has(h.id));
        this.recompute();
        if (ended) void this.refresh();
      }),
    );
  }

  private get now(): number {
    return this.ws.deps.clock.now();
  }

  private recompute(): void {
    const cache = this.ws.state.cache;
    this.knownHuddles = cache?.huddles;
    const settings = huddleLinkSettings(cache?.workspace.huddleLinks);
    this.update({
      sections: huddlesSections(this.overview, Object.values(cache?.huddles ?? {}), this.ws.state.meId, this.now),
      policyChoices: meetingPolicyChoices(settings),
      linksOn: settings.links,
      chatOn: isFeatureEnabled(cache?.workspace.features, "chat"),
    });
  }

  refresh(): Promise<void> {
    if (this.inFlight !== undefined) return this.inFlight;
    this.inFlight = this.api.overview(this.ws.wsId).then(
      (o) => {
        if (this.isDisposed) return;
        this.overview = o;
        this.update({ status: "ready" });
        this.recompute();
      },
      () => {
        if (!this.isDisposed && this.state.status === "loading") this.update({ status: "failed" });
      },
    );
    return this.inFlight.finally(() => {
      this.inFlight = undefined;
    });
  }

  // ---------- Joining ----------

  /** Joins a live huddle (its conversation's, or a link huddle). */
  join(channelKey: string): HuddleDestination {
    if (this.joinHere) void this.call.join(channelKey);
    return { kind: "call", channelKey };
  }

  /** Joins a link's huddle (mine or one I was invited to). */
  joinLink(meeting: Meeting): HuddleDestination {
    return this.join(meeting.channelId ?? `meeting:${meeting.code}`);
  }

  setJoinText(text: string): void {
    this.update({ joinText: text, joinError: undefined });
  }

  /**
   * "Join": a link of this workspace I know joins here; any other link or code opens its page (another workspace's
   * link, or one I'm not invited to, asks there). Undefined when the text isn't a link or code.
   */
  submitJoin(webOrigin: string): HuddleDestination | undefined {
    const code = parseMeetingCode(this.state.joinText);
    if (code === null) {
      this.update({ joinError: msg("huddles.join.invalid") });
      return undefined;
    }
    this.update({ joinText: "", joinError: undefined });
    const mine = this.overview.links.find((m) => m.code === code && !m.revoked);
    if (mine !== undefined) return this.joinLink(mine);
    const live = this.state.sections.live.find((h) => h.meeting?.code === code);
    if (live !== undefined) return this.join(live.channelId);
    return { kind: "page", url: `${webOrigin.replace(/\/+$/, "")}/m/${code}` };
  }

  /** The link's huddle is live now. */
  isLive(meeting: Meeting): boolean {
    return liveHuddleOf(meeting, this.state.sections.live) !== undefined;
  }

  // ---------- Get a link / Schedule ----------

  /**
   * Opens "Get a link to share" (the link is made right away, named `defaultTitle` until renamed, like Google Meet's
   * "Create a meeting for later") or "Schedule" (`today` is the date it starts on, "YYYY-MM-DD").
   */
  openLink(mode: "link" | "schedule", today: string, defaultTitle: string): void {
    const settings = huddleLinkSettings(this.ws.state.cache?.workspace.huddleLinks);
    // Schedule starts at the next half hour (Google Calendar's default), local time.
    const at = new Date(Math.ceil((this.now + 1) / HALF_HOUR_MS) * HALF_HOUR_MS);
    const pad = (n: number) => String(n).padStart(2, "0");
    const when = mode === "schedule" ? { date: `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`, time: `${pad(at.getHours())}:${pad(at.getMinutes())}` } : { date: today, time: "" };
    this.update({ link: { mode, title: "", ...when, policy: defaultMeetingPolicy(settings), invitees: [], busy: false } });
    if (mode === "link") void this.createLink(defaultTitle);
  }

  editLink(patch: Partial<Pick<HuddleLinkForm, "title" | "date" | "time" | "policy" | "invitees">>): void {
    const form = this.state.link;
    if (form === undefined) return;
    this.update({ link: { ...form, ...patch, error: undefined } });
    // On a link that exists, the name and "Who can join" save as they change.
    if (form.created !== undefined && (patch.policy !== undefined || patch.title !== undefined)) void this.saveCreated(patch);
  }

  toggleInvitee(userId: string): void {
    const form = this.state.link;
    if (form === undefined || form.created !== undefined) return;
    const invitees = form.invitees.includes(userId) ? form.invitees.filter((u) => u !== userId) : [...form.invitees, userId];
    this.update({ link: { ...form, invitees } });
  }

  closeLink(): void {
    this.update({ link: undefined });
  }

  /** The request "Get a link" / "Schedule" sends (undefined while the form isn't complete). */
  linkRequest(defaultTitle?: string): CreateMeetingRequest | undefined {
    const form = this.state.link;
    if (form === undefined) return undefined;
    const title = form.title.trim() || defaultTitle?.trim() || "";
    if (title === "" || title.length > 120) return undefined;
    const req: CreateMeetingRequest = { title, policy: form.policy };
    if (form.mode === "schedule") {
      const at = localDateTime(form.date, form.time);
      if (at === undefined || at <= this.now) return undefined;
      req.scheduledAt = at;
    }
    if (form.invitees.length > 0) req.invitees = form.invitees;
    return req;
  }

  /** Makes the link ("Get a link" right away with a placeholder name the person can change; "Schedule" on save). */
  async createLink(defaultTitle?: string): Promise<void> {
    const form = this.state.link;
    if (form === undefined || form.busy || form.created !== undefined) return;
    const req = this.linkRequest(defaultTitle);
    if (req === undefined) return;
    this.update({ link: { ...form, busy: true, error: undefined } });
    try {
      const created = await this.api.createMeeting(this.ws.wsId, req);
      this.overview = { ...this.overview, links: [created, ...this.overview.links] };
      this.recompute();
      const now = this.state.link;
      if (now !== undefined) this.update({ link: { ...now, busy: false, created, title: created.title } });
    } catch {
      const now = this.state.link;
      if (now !== undefined) this.update({ link: { ...now, busy: false, error: msg("huddles.link.error") } });
    }
  }

  /** "Start now" on a link that was just made: joins its huddle here. */
  startLink(): HuddleDestination | undefined {
    const created = this.state.link?.created;
    this.closeLink();
    return created === undefined ? undefined : this.joinLink(created);
  }

  private async saveCreated(patch: { title?: string; policy?: MeetingPolicy }): Promise<void> {
    const created = this.state.link?.created;
    if (created === undefined) return;
    const req: { title?: string; policy?: MeetingPolicy } = {};
    if (patch.title !== undefined && patch.title.trim() !== "") req.title = patch.title.trim();
    if (patch.policy !== undefined) req.policy = patch.policy;
    if (Object.keys(req).length === 0) return;
    try {
      const saved = await this.api.updateMeeting(this.ws.wsId, created.code, req);
      this.replaceLink(saved);
      const form = this.state.link;
      if (form?.created?.code === saved.code) this.update({ link: { ...form, created: saved } });
    } catch {
      const form = this.state.link;
      if (form !== undefined) this.update({ link: { ...form, error: msg("huddles.link.error") } });
    }
  }

  /** My links: change who can join. */
  async setPolicy(code: string, policy: MeetingPolicy): Promise<void> {
    try {
      this.replaceLink(await this.api.updateMeeting(this.ws.wsId, code, { policy }));
    } catch {
      void this.refresh();
    }
  }

  /** My links: stop a link from working (people from outside who joined from it leave the call). */
  async revoke(code: string): Promise<void> {
    const before = this.overview;
    this.overview = { ...before, links: before.links.map((m) => (m.code === code ? { ...m, revoked: true } : m)) };
    this.recompute();
    try {
      this.replaceLink(await this.api.updateMeeting(this.ws.wsId, code, { revoked: true }));
    } catch {
      this.overview = before;
      this.recompute();
    }
  }

  /** Settings → Huddles "Revoke all links" (owners and admins): every link in the workspace stops working. */
  async revokeAll(): Promise<number> {
    const revoked = await this.api.revokeMeetings(this.ws.wsId);
    if (this.isDisposed) return revoked;
    this.overview = { ...this.overview, links: this.overview.links.map((m) => ({ ...m, revoked: true })) };
    this.recompute();
    return revoked;
  }

  private replaceLink(m: Meeting): void {
    this.overview = { ...this.overview, links: this.overview.links.map((x) => (x.code === m.code ? m : x)) };
    this.recompute();
  }

  // ---------- Start a huddle ----------

  openStart(): void {
    this.update({ start: { userIds: [], query: "", busy: false } });
  }

  closeStart(): void {
    this.update({ start: undefined });
  }

  setStartQuery(query: string): void {
    const s = this.state.start;
    if (s !== undefined) this.update({ start: { ...s, query } });
  }

  toggleStartPerson(userId: string): void {
    const s = this.state.start;
    if (s === undefined || userId === this.ws.state.meId) return;
    const userIds = s.userIds.includes(userId) ? s.userIds.filter((u) => u !== userId) : [...s.userIds, userId];
    this.update({ start: { ...s, userIds, query: "", error: undefined } });
  }

  setStartChannel(channelId: string | undefined): void {
    const s = this.state.start;
    if (s === undefined) return;
    const next: HuddleStartForm = { ...s, query: "", error: undefined };
    if (channelId === undefined) delete next.channelId;
    else next.channelId = channelId;
    this.update({ start: next });
  }

  /**
   * "Start": with Chat on, a channel's huddle or the DM's with the people picked (Slack). With Chat off, or nobody
   * picked, a link huddle: the people picked are invited (it shows in their Huddles). `defaultTitle`: the name of a
   * link huddle when there's nobody to name it after ("Huddle").
   */
  async startHuddle(defaultTitle: string): Promise<HuddleDestination | undefined> {
    const s = this.state.start;
    if (s === undefined || s.busy) return undefined;
    this.update({ start: { ...s, busy: true, error: undefined } });
    try {
      let key: string | undefined;
      if (this.state.chatOn && s.channelId !== undefined) {
        key = s.channelId;
      } else if (this.state.chatOn && s.userIds.length > 0) {
        const c = await this.ws.deps.data.createChannel(this.ws.wsId, { kind: s.userIds.length > 1 ? "group_dm" : "dm", name: "", memberIds: s.userIds });
        key = c.id;
      } else {
        const cache = this.ws.state.cache;
        const names = cache === undefined ? [] : s.userIds.map((u) => memberName(cache, u)).filter((n) => n !== "");
        const title = (names.length > 0 ? [this.myName(), ...names].join(", ") : defaultTitle).slice(0, 120);
        const req: CreateMeetingRequest = { title };
        if (s.userIds.length > 0) req.invitees = s.userIds;
        const created = await this.api.createMeeting(this.ws.wsId, req);
        this.overview = { ...this.overview, links: [created, ...this.overview.links] };
        this.recompute();
        key = `meeting:${created.code}`;
      }
      this.update({ start: undefined });
      return this.join(key);
    } catch {
      const now = this.state.start;
      if (now !== undefined) this.update({ start: { ...now, busy: false, error: msg("huddles.start.error") } });
      return undefined;
    }
  }

  private myName(): string {
    const cache = this.ws.state.cache;
    return cache === undefined ? "" : memberName(cache, this.ws.state.meId);
  }

  // ---------- A past huddle ----------

  async openRecord(huddleId: string): Promise<void> {
    if (this.state.record?.huddleId === huddleId && this.state.record.status !== "failed") return;
    this.cancelNotesPoll?.();
    this.cancelNotesPoll = undefined;
    this.update({ record: { huddleId, status: "loading" } });
    await this.loadRecord(huddleId, 0);
  }

  /** Loads the page; while its AI notes are being written, again every NOTES_POLL_MS (at most NOTES_POLLS times). */
  private async loadRecord(huddleId: string, polls: number): Promise<void> {
    try {
      const data = await this.api.record(this.ws.wsId, huddleId);
      if (this.state.record?.huddleId !== huddleId) return;
      this.update({ record: { huddleId, status: "ready", data } });
      if (data.notesPending !== true || polls >= NOTES_POLLS) return;
      this.cancelNotesPoll = this.ws.deps.clock.setTimer(NOTES_POLL_MS, () => {
        this.cancelNotesPoll = undefined;
        void this.loadRecord(huddleId, polls + 1);
      });
    } catch {
      if (polls === 0 && this.state.record?.huddleId === huddleId) this.update({ record: { huddleId, status: "failed" } });
    }
  }
}
